<script setup lang="ts">
import { computed, ref } from "vue";
import { useSkillMetrics } from "~/composables/useSkillMetrics";
import { useSkillCategories } from "~/composables/useSkillCategories";
import type { SkillTier } from "~/composables/useSkillCategories";
import { useI18n } from "~/composables/useI18n";
import SkillCard from "../SkillCard/SkillCard.vue";
import { useResumeStore } from "~/stores/useResumeStore";
import { storeToRefs } from "pinia";
import type { SkillCategory } from "~/typings/database";

const t = useI18n();

const store = useResumeStore();
const { skills, experiences } = storeToRefs(store);

const skillsWithMetrics = useSkillMetrics(
  computed(() =>
    skills.value.filter((s) => !s.categories.includes("soft" as SkillCategory))
  ),
  experiences
);
const { byTier, tiers } = useSkillCategories(skillsWithMetrics);

type FilterTab = "all" | SkillTier;

const activeFilter = ref<FilterTab>("all");

const filterTabs: { label: string; value: FilterTab }[] = [
  { label: t("skillsPage.filterAll"), value: "all" },
  ...tiers.map((tier) => ({
    label: t(`skillsPage.tiers.${tier}`),
    value: tier as FilterTab,
  })),
];

const visibleTiers = computed<SkillTier[]>(() => {
  if (activeFilter.value === "all") {
    return tiers;
  }
  return [activeFilter.value as SkillTier];
});
</script>

<template>
  <section :id="'tools'" aria-label="Skills catalog">
    <!-- Filter tabs -->
    <div
      role="tablist"
      aria-label="Filter skills by tier"
      class="flex flex-wrap gap-2 mb-6"
    >
      <UiButton
        v-for="tab in filterTabs"
        :key="tab.value"
        role="tab"
        :aria-selected="activeFilter === tab.value"
        color="primary"
        :variant="activeFilter === tab.value ? 'solid' : 'outline'"
        class="capitalize"
        @click="activeFilter = tab.value"
        @keydown.enter="activeFilter = tab.value"
        @keydown.space.prevent="activeFilter = tab.value"
      >
        {{ tab.label }}
      </UiButton>
    </div>

    <!-- Skills by tier -->
    <div class="space-y-8">
      <div
        v-for="tier in visibleTiers"
        :key="tier"
        role="tabpanel"
        :aria-label="`${t(`skillsPage.tiers.${tier}`)} skills`"
      >
        <UiTitle
          level="4"
          class="text-base font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3"
        >
          {{ t(`skillsPage.tiers.${tier}`) }}
          <span
            class="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400"
          >
            ({{ byTier[tier].length }})
          </span>
        </UiTitle>
        <div
          v-if="byTier[tier].length > 0"
          class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
        >
          <SkillCard
            v-for="skill in byTier[tier]"
            :key="skill.id"
            :skill="skill"
          />
        </div>
        <p v-else class="text-sm text-gray-500 dark:text-gray-400">
          {{ t("skillsPage.noSkillsInCategory") }}
        </p>
      </div>
    </div>
  </section>
</template>
